import { z } from 'zod'

/**
 * An interactive shell. The session lives in the backend (PROMPT.md §5.6), so a tab survives
 * navigating around the app and reattaches with its recent output intact.
 */
export const terminalSessionSchema = z.object({
  id: z.string(),
  serverId: z.string(),
  title: z.string(),
  cols: z.number().int().positive(),
  rows: z.number().int().positive(),
  status: z.enum(['open', 'closed']),
  createdAt: z.iso.datetime(),
  closedReason: z.string().nullable(),
})
export type TerminalSession = z.infer<typeof terminalSessionSchema>

const geometry = {
  cols: z.number().int().min(1).max(2000),
  rows: z.number().int().min(1).max(2000),
}

export const createTerminalSessionSchema = z.object({
  serverId: z.string().min(1),
  cols: geometry.cols.default(80),
  rows: geometry.rows.default(24),
})
export type CreateTerminalSessionInput = z.infer<typeof createTerminalSessionSchema>

export const terminalSessionParamsSchema = z.object({ id: z.string().min(1) })

/**
 * Control messages on the terminal socket. Keystrokes and output travel as binary frames — the
 * reason PROMPT.md §3.2 picks `ws` over Socket.IO — while anything structured goes as JSON text.
 */
export const terminalClientMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('resize'), ...geometry }),
  z.object({ type: z.literal('ping') }),
])
export type TerminalClientMessage = z.infer<typeof terminalClientMessageSchema>

export const terminalServerMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('attached'), session: terminalSessionSchema }),
  z.object({ type: z.literal('exit'), reason: z.string() }),
  z.object({ type: z.literal('error'), message: z.string() }),
])
export type TerminalServerMessage = z.infer<typeof terminalServerMessageSchema>
